import { ACCESSIBILITY_PATH, SITE_PAGES } from '@hunk/shared/api/pages';
import { lazy, Suspense, useEffect } from 'react';
import { Navigate, Outlet, Route, Routes, useLocation } from 'react-router';

import { CartToast } from '@/components/CartToast';
import { Logo } from '@/components/Logo';
import { SiteFooter, SiteHeader } from '@/components/SiteHeader';
import { useAppNavigate } from '@/hooks/useAppNavigate';
import { CartProvider } from '@/hooks/useCart';
import { useSession } from '@/hooks/useSession';
import { ShopProvider } from '@/hooks/useShop';
import { CartPage } from '@/pages/CartPage';
import { CheckoutPage } from '@/pages/CheckoutPage';
import { HomePage, NotFound } from '@/pages/HomePage';
import { LoginPage } from '@/pages/LoginPage';
import { OrderPage } from '@/pages/OrderPage';
import { OrdersPage } from '@/pages/OrdersPage';
import { ProductPage } from '@/pages/ProductPage';

// admin screens load only when an admin opens them - keeps them out of the main bundle
const AdminLayout = lazy(() => import('@/components/admin/AdminLayout').then((m) => ({ default: m.AdminLayout })));
const AdminOrdersPage = lazy(() => import('@/pages/admin/AdminOrdersPage').then((m) => ({ default: m.AdminOrdersPage })));
const AdminProductsPage = lazy(() => import('@/pages/admin/AdminProductsPage').then((m) => ({ default: m.AdminProductsPage })));
const CategoriesPage = lazy(() => import('@/pages/admin/CategoriesPage').then((m) => ({ default: m.CategoriesPage })));
const UsersPage = lazy(() => import('@/pages/admin/UsersPage').then((m) => ({ default: m.UsersPage })));
const TransactionsPage = lazy(() => import('@/pages/admin/TransactionsPage').then((m) => ({ default: m.TransactionsPage })));
const PaymentsPage = lazy(() => import('@/pages/admin/PaymentsPage').then((m) => ({ default: m.PaymentsPage })));
const PagesAdminPage = lazy(() => import('@/pages/admin/PagesAdminPage').then((m) => ({ default: m.PagesAdminPage })));
// content pages (about, guides) - with the Markdown renderer, loaded only when opened
const ContentPage = lazy(() => import('@/pages/ContentPage').then((m) => ({ default: m.ContentPage })));
const ShopSettingsPage = lazy(() => import('@/pages/admin/ShopSettingsPage').then((m) => ({ default: m.ShopSettingsPage })));

/** removes the launch screen of index.html (fades out) */
function hideLaunch() {
  const el = document.getElementById('launch');
  if (!el) return;
  el.classList.add('launch-done');
  setTimeout(() => el.remove(), 400);
}

/**
 * The shop is open to everyone; checkout, orders and admin need a login.
 * Mini App: always logged in through Telegram; only if that fails is the Mini App login screen shown.
 */
export function App() {
  const { status, isMiniApp, session } = useSession();
  const navigate = useAppNavigate();
  const { pathname } = useLocation();

  // Mini App opened with ?startapp=order_<id> (back from a payment in the browser / the bot) -> that order
  useEffect(() => {
    const sp = isMiniApp ? window.Telegram?.WebApp?.initDataUnsafe?.start_param : undefined;
    if (status !== 'authenticated' || !sp) return;
    if (sp.startsWith('order_')) navigate(`/order/${encodeURIComponent(sp.slice(6))}`, { replace: true });
    else if (sp === 'orders') navigate('/orders', { replace: true });
  }, [status, isMiniApp, navigate]);

  // back from "Log in with Telegram" (it returns to /) -> where the login started (checkout...)
  useEffect(() => {
    if (status !== 'authenticated' || pathname !== '/') return;
    try {
      const next = sessionStorage.getItem('hunk.next');
      sessionStorage.removeItem('hunk.next');
      if (next && next !== '/' && next.startsWith('/') && !next.startsWith('//')) navigate(next, { replace: true });
    } catch {
      // private mode
    }
  }, [status, pathname, navigate]);

  const isAdmin = status === 'authenticated' && session?.role === 'admin';
  // the launch screen stays until the session is known - from then on the app shows its own screens
  useEffect(() => {
    if (status !== 'loading') hideLaunch();
  }, [status]);

  if (status === 'loading') {
    return (
      <main className="flex min-h-svh items-center justify-center">
        <Logo size="lg" className="animate-pulse" />
      </main>
    );
  }
  if (isMiniApp && status !== 'authenticated') return <LoginPage />;

  return (
    <ShopProvider>
      <CartProvider>
        <Routes>
          <Route path="/login" element={isMiniApp ? <Navigate to="/" replace /> : <LoginPage />} />
          <Route element={<SiteLayout />}>
            <Route path="/" element={<HomePage />} />
            <Route path="/category/:slug" element={<HomePage />} />
            {/* /מוצר/<id>-<name> (the links) and /product/<id>-<latin name> - see productUrl.ts */}
            <Route path="/מוצר/:id" element={<ProductPage />} />
            <Route path="/product/:id" element={<ProductPage />} />
            <Route path="/cart" element={<CartPage />} />
            <Route path="/checkout" element={<CheckoutPage />} />
            <Route path="/orders" element={<OrdersPage />} />
            <Route path="/order/:id" element={<OrderPage />} />
            {/* the old fixed statement page -> the editable one */}
            <Route path="/accessibility" element={<Navigate to={ACCESSIBILITY_PATH} replace />} />
            {SITE_PAGES.map((p) => (
              <Route key={p.key} path={p.path} element={<ContentPage pageKey={p.key} />} />
            ))}
            <Route path="/admin" element={isAdmin ? <AdminLayout /> : status === 'anonymous' ? <Navigate to="/login?next=/admin" replace /> : <Navigate to="/" replace />}>
              <Route index element={<Navigate to="/admin/orders" replace />} />
              <Route path="orders" element={<AdminOrdersPage />} />
              <Route path="products" element={<AdminProductsPage />} />
              <Route path="categories" element={<CategoriesPage />} />
              <Route path="users" element={<UsersPage />} />
              <Route path="transactions" element={<TransactionsPage />} />
              <Route path="payments" element={<PaymentsPage />} />
              <Route path="pages" element={<PagesAdminPage />} />
              <Route path="settings" element={<ShopSettingsPage />} />
            </Route>
            <Route path="*" element={<NotFound />} />
          </Route>
        </Routes>
        <CartToast />
      </CartProvider>
    </ShopProvider>
  );
}

function SiteLayout() {
  return (
    <div className="flex min-h-svh flex-col">
      <SiteHeader />
      <div className="flex-1">
        <Suspense fallback={<div className="p-8 text-center text-sm text-neutral-500">טוען...</div>}>
          <Outlet />
        </Suspense>
      </div>
      <SiteFooter />
    </div>
  );
}
